import { supabase } from '@/lib/supabaseClient';
import { CycleStatus, OperationType } from '@/types/database';
import { ledgerService, GeneralLedgerEntry } from '@/services/ledgerService';
import * as XLSX from 'xlsx';

export interface CyclePerformanceData {
  cycleId: string;
  cycleCode: string;
  batchCode: string;
  growerName: string;
  farmName: string;
  farmId?: string | null;
  growerId?: string | null;
  status: CycleStatus;
  operationType: OperationType;
  startDate: string;
  endDate: string | null;
  daysOnFeed: number | null;
  placedHead: number | null;
  soldHead: number;
  mortalityHead: number | null;
  mortalityRatePct: number | null;
  feedBags: number;
  feedKg: number;
  feedCost: number;
  pigletCost: number;
  medCost: number;
  otherCosts: number;
  totalSoldWeightKg: number | null;
  totalRevenue: number;
  totalProductionCost: number;
  grossProfit: number;
  grossMarginPct: number | null;
  growerNetPayout: number | null;
}

export interface RaiserPerformanceData {
  growerName: string;
  totalCycles: number;
  cycleCodes: string[];
  totalPlacedHead: number | null;
  totalSoldHead: number;
  totalSoldWeightKg: number | null;
  totalFeedBags: number;
  totalFeedCost: number;
  totalPigletCost: number;
  totalMedCost: number;
  totalOtherCosts: number;
  totalRevenue: number;
  totalProductionCost: number;
  netGrossProfit: number;
  avgGrossMarginPct: number | null;
  totalGrowerPayout: number;
}

export interface BatchComparisonPoint {
  batchCode: string;
  revenue: number;
  productionCost: number;
  feedCost: number;
  pigletCost: number;
  medCost: number;
  otherCost: number;
  grossProfit: number;
  marginPct: number | null;
  cycleCount: number;
  soldHead: number;
}

export interface ProductionCostMixCategory {
  category: 'feed' | 'piglets' | 'medication' | 'other';
  label: string;
  accountCodes: string;
  amount: number;
  pctOfTotalCosts: number;
  priorYearAmount: number | null;
  changePct: number | null;
}

export interface GrowerSettlementSummaryPosition {
  finalizedCount: number;
  approvedCount: number;
  draftCount: number;
  amountOutstanding: number;
}

export interface CompletedCyclePerformanceSummary {
  completedCycles: CyclePerformanceData[];
  bestMarginCycle: { cycleCode: string; growerName: string; grossMarginPct: number } | null;
  lowestMarginCycle: { cycleCode: string; growerName: string; grossMarginPct: number } | null;
  lowestMortalityCycle: { cycleCode: string; growerName: string; mortalityRatePct: number; placedHead: number; mortalityHead: number } | null;
  completedCyclesGrossProfit: number;
  completedCyclesGrossMarginPct: number | null;
  completedCyclesRevenue: number;
  completedCyclesCost: number;
}

export interface ManagementAttentionSummary {
  needsReviewCount: number;
  incompleteRecordsCount: number;
  outstandingSettlementsCount: number;
  unresolvedCycleIssuesCount: number;
}

export interface ManagementSummaryData {
  selectedYear: string;

  // 1. Financial Snapshot
  totalBookedRevenue: number;
  totalBookedProductionCosts: number;
  netBookedMovement: number;
  completedCyclesGrossProfit: number;
  completedCyclesGrossMarginPct: number | null;
  completedCyclesRevenue: number;
  completedCyclesCost: number;
  overallClosedGrossMarginPct: number | null; // Backward compatibility

  // 2. Current Production
  activeCyclesCount: number;
  livePigsOnFeed: number;
  totalPigsSold: number;
  totalMortalityHead: number;
  hasValidMortalityRate: boolean;
  overallMortalityRatePct: number | null;

  // 3. Grower Settlement Position
  settlementPosition: GrowerSettlementSummaryPosition;

  // 4. Completed Cycle Performance
  completedPerformance: CompletedCyclePerformanceSummary;

  // 5. Production Cost Mix & Movement
  costMix: ProductionCostMixCategory[];

  // 6. Management Attention
  managementAttention: ManagementAttentionSummary;

  // Legacy / Batch comparison fields
  totalFeedAllocatedCost: number;
  totalFeedBags: number;
  totalCyclesCount: number;
  batchComparison: BatchComparisonPoint[];
  priorYear?: {
    year: string;
    totalBookedRevenue: number;
    totalBookedProductionCosts: number;
    netBookedMovement: number;
    revenueGrowthPct: number | null;
    costGrowthPct: number | null;
    totalPigsSold: number;
    totalFeedAllocatedCost: number;
  } | null;
}

export interface AnnualFinancialSummaryRecord {
  year: number;
  pigSales: number;
  totalBookedCosts: number;
  netBookedMovement: number;
  grossMarginPct: number | null;
  activeMonthsCount: number;
}

export interface MonthlyFinancialActivityRecord {
  monthKey: string;
  monthLabel: string;
  year: number;
  month: number;
  pigSales: number;
  salesRevenue: number;
  piglets: number;
  pigletCost: number;
  feeds: number;
  feedCost: number;
  medsAndDirectExpenses: number;
  medCost: number;
  totalBookedCosts: number;
  totalProductionCost: number;
  netBookedMovement: number;
  grossProfit: number;
  grossMarginPct: number | null;
  adjustments: number;
  headSold: number | null;
  feedBags: number | null;
  momRevenueGrowthPct: number | null;
  momCostGrowthPct: number | null;
  yoyRevenueGrowthPct: number | null;
  yoyCostGrowthPct: number | null;
  accountBreakdown: Record<string, { code: string; name: string; sum: number; count: number }>;
}

export type MonthlyFinancialRecord = MonthlyFinancialActivityRecord;

export interface ReportFilters {
  selectedYear?: string | null;
  batchCode?: string | null;
  farmId?: string | null;
  growerId?: string | null;
  status?: string | null;
  operationType?: string | null;
  startDateFrom?: string | null;
  startDateTo?: string | null;
  saleDateFrom?: string | null;
  saleDateTo?: string | null;
  search?: string | null;
}

/**
 * Pure helper to calculate days on feed between placement and completion.
 * Returns null if missing or unrealistic.
 */
export function calculateDaysBetween(
  startDateStr: string | null | undefined,
  endDateStr: string | null | undefined,
  isOngoingCycle = false
): number | null {
  if (!startDateStr) return null;
  const start = new Date(startDateStr);
  if (isNaN(start.getTime())) return null;

  let end: Date;
  if (isOngoingCycle) {
    end = new Date();
  } else if (endDateStr) {
    end = new Date(endDateStr);
  } else {
    return null;
  }

  if (isNaN(end.getTime())) return null;

  const diffMs = end.getTime() - start.getTime();
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  return days >= 0 && days <= 365 ? days : null;
}

/**
 * Pure calculation function for single cycle performance.
 * Preserves strict physical integrity: nulls whenever unmeasured.
 */
export function computeSingleCyclePerformance(
  cycle: {
    id: string;
    cycle_code: string;
    batch_code: string;
    grower_id?: string | null;
    farm_id?: string | null;
    operation_type: OperationType;
    status: CycleStatus;
    start_date: string;
    expected_sale_date?: string | null;
    actual_close_date?: string | null;
    growers?: { full_name: string } | null;
    farms?: { farm_name: string } | null;
    legacy_raiser_name?: string | null;
  },
  arrivals: Array<{ head_count?: number; total_weight_kg?: number; total_cost?: number; is_void: boolean }> = [],
  mortalities: Array<{ head_count?: number; is_void: boolean }> = [],
  transfersIn: Array<{ head_count?: number; destination_cycle_id?: string | null; total_transferred_cost?: number; total_cost?: number; is_void: boolean }> = [],
  transfersOut: Array<{ head_count?: number; source_cycle_id?: string | null; total_transferred_cost?: number; total_cost?: number; is_void: boolean }> = [],
  feedReceipts: Array<{ quantity_bags?: number; total_cost?: number; is_void: boolean }> = [],
  medicineReceipts: Array<{ total_amount?: number; is_void: boolean }> = [],
  expenses: Array<{ amount?: number; account_code?: string; is_void: boolean }> = [],
  sales: Array<{ id: string; sale_date?: string; head_count?: number; net_weight_kg?: number; total_amount?: number; is_void: boolean }> = [],
  settlements: Array<{
    status: string;
    settlement_date?: string;
    total_sales_revenue?: number;
    total_feed_cost?: number;
    total_piglet_cost?: number;
    total_med_cost?: number;
    total_other_costs?: number;
    gross_profit?: number;
    grower_net_payout?: number;
    is_void: boolean;
  }> = []
): CyclePerformanceData {
  const activeArrivals = (arrivals || []).filter((a) => !a.is_void);
  const activeMortalities = (mortalities || []).filter((m) => !m.is_void);
  const activeTransfersIn = (transfersIn || []).filter((t) => !t.is_void && t.destination_cycle_id === cycle.id);
  const activeTransfersOut = (transfersOut || []).filter((t) => !t.is_void && t.source_cycle_id === cycle.id);
  const activeFeedReceipts = (feedReceipts || []).filter((f) => !f.is_void);
  const activeMedReceipts = (medicineReceipts || []).filter((m) => !m.is_void);
  const activeExpenses = (expenses || []).filter((e) => !e.is_void);
  const activeSales = (sales || []).filter((s) => !s.is_void);
  const activeSettlements = (settlements || []).filter((s) => !s.is_void);

  // Placed Head: Only if arrivals exist
  const rawPigsReceived = activeArrivals.reduce((sum, a) => sum + (Number(a.head_count) || 0), 0);
  const placedHead = activeArrivals.length > 0 && rawPigsReceived > 0 ? rawPigsReceived : null;

  // Mortality Head
  const rawMortalityHead = activeMortalities.reduce((sum, m) => sum + (Number(m.head_count) || 0), 0);
  const mortalityHead = activeMortalities.length > 0 ? rawMortalityHead : null;
  const mortalityRatePct = placedHead && placedHead > 0 && mortalityHead !== null
    ? Number(((mortalityHead / placedHead) * 100).toFixed(2))
    : null;

  // Sold Head & Weight
  const soldHead = activeSales.reduce((sum, s) => sum + (Number(s.head_count) || 0), 0);
  const rawSoldWeightKg = activeSales.reduce((sum, s) => sum + (Number(s.net_weight_kg) || 0), 0);
  const totalSoldWeightKg = activeSales.length > 0 && rawSoldWeightKg > 0 ? rawSoldWeightKg : null;

  // Dates & Days on Feed
  const latestSaleDate = activeSales.length > 0
    ? activeSales.map((s) => s.sale_date).filter(Boolean).sort().reverse()[0]
    : null;
  const latestSettlementDate = activeSettlements.length > 0
    ? activeSettlements.map((s) => s.settlement_date).filter(Boolean).sort().reverse()[0]
    : null;
  const completionDate = cycle.actual_close_date || latestSaleDate || latestSettlementDate || null;

  const isOngoing = cycle.status === 'active';
  const daysOnFeed = calculateDaysBetween(cycle.start_date, completionDate, isOngoing);
  const endDate = isOngoing ? null : completionDate;

  // Feeds (Direct Deliveries + Transfers In - Transfers Out)
  const directFeedBags = activeFeedReceipts.reduce((sum, r) => sum + (Number(r.quantity_bags) || 0), 0);
  const directFeedCost = activeFeedReceipts.reduce((sum, r) => sum + (Number(r.total_cost) || 0), 0);
  const transfersInCost = activeTransfersIn.reduce((sum, t) => sum + (Number(t.total_transferred_cost || t.total_cost) || 0), 0);
  const transfersOutCost = activeTransfersOut.reduce((sum, t) => sum + (Number(t.total_transferred_cost || t.total_cost) || 0), 0);

  const feedBags = directFeedBags;
  const feedKg = feedBags * 50;
  const feedCostOperational = directFeedCost + transfersInCost - transfersOutCost;

  // Costs (Operational fallback to Settlement snapshot if available)
  const pigArrivalCost = activeArrivals.reduce((sum, a) => sum + (Number(a.total_cost) || 0), 0) ||
    activeSettlements.reduce((sum, st) => sum + (Number(st.total_piglet_cost) || 0), 0);

  const feedCost = feedCostOperational > 0
    ? feedCostOperational
    : activeSettlements.reduce((sum, st) => sum + (Number(st.total_feed_cost) || 0), 0);

  const medCost = activeMedReceipts.reduce((sum, m) => sum + (Number(m.total_amount) || 0), 0) ||
    activeSettlements.reduce((sum, st) => sum + (Number(st.total_med_cost) || 0), 0);

  const otherExpensesCost = activeExpenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0) ||
    activeSettlements.reduce((sum, st) => sum + (Number(st.total_other_costs) || 0), 0);

  // Sales Revenue
  const salesRevenue = activeSales.reduce((sum, s) => sum + (Number(s.total_amount) || 0), 0);
  const settlementSalesRevenue = activeSettlements.reduce((sum, st) => sum + (Number(st.total_sales_revenue) || 0), 0);
  const totalRevenue = salesRevenue > 0 ? salesRevenue : settlementSalesRevenue;

  const totalProductionCost = pigArrivalCost + feedCost + medCost + otherExpensesCost;
  const grossProfit = totalRevenue - totalProductionCost;
  const grossMarginPct = totalRevenue > 0 ? Number(((grossProfit / totalRevenue) * 100).toFixed(2)) : null;

  const settledRecord = activeSettlements.find((st) =>
    ['frozen', 'approved', 'draft', 'settled'].includes(st.status)
  );
  const growerNetPayout = settledRecord?.grower_net_payout !== undefined && settledRecord?.grower_net_payout !== null
    ? Number(settledRecord.grower_net_payout)
    : null;

  const growerName = cycle.growers?.full_name || cycle.legacy_raiser_name || 'VSB Partner';
  const farmName = cycle.farms?.farm_name || 'Sorosoro Ibaba';

  return {
    cycleId: cycle.id,
    cycleCode: cycle.cycle_code,
    batchCode: cycle.batch_code,
    growerName,
    farmName,
    farmId: cycle.farm_id,
    growerId: cycle.grower_id,
    status: cycle.status,
    operationType: cycle.operation_type,
    startDate: cycle.start_date,
    endDate,
    daysOnFeed,
    placedHead,
    soldHead,
    mortalityHead,
    mortalityRatePct,
    feedBags,
    feedKg,
    feedCost,
    pigletCost: pigArrivalCost,
    medCost,
    otherCosts: otherExpensesCost,
    totalSoldWeightKg,
    totalRevenue,
    totalProductionCost,
    grossProfit,
    grossMarginPct,
    growerNetPayout,
  };
}

/**
 * Aggregates performance by unique Raiser across all cycles.
 */
export function aggregatePerformanceByRaiser(cycles: CyclePerformanceData[]): RaiserPerformanceData[] {
  const raiserMap = new Map<string, {
    cycles: CyclePerformanceData[];
    totalPlacedHead: number;
    hasPlacedHead: boolean;
    totalSoldHead: number;
    totalSoldWeightKg: number;
    hasSoldWeight: boolean;
    totalFeedBags: number;
    totalFeedCost: number;
    totalPigletCost: number;
    totalMedCost: number;
    totalOtherCosts: number;
    totalRevenue: number;
    totalProductionCost: number;
    netGrossProfit: number;
    totalGrowerPayout: number;
  }>();

  for (const c of cycles) {
    const key = c.growerName || 'Unknown Raiser';
    const cur = raiserMap.get(key) || {
      cycles: [],
      totalPlacedHead: 0,
      hasPlacedHead: false,
      totalSoldHead: 0,
      totalSoldWeightKg: 0,
      hasSoldWeight: false,
      totalFeedBags: 0,
      totalFeedCost: 0,
      totalPigletCost: 0,
      totalMedCost: 0,
      totalOtherCosts: 0,
      totalRevenue: 0,
      totalProductionCost: 0,
      netGrossProfit: 0,
      totalGrowerPayout: 0,
    };

    cur.cycles.push(c);
    if (c.placedHead !== null && c.placedHead > 0) {
      cur.totalPlacedHead += c.placedHead;
      cur.hasPlacedHead = true;
    }
    cur.totalSoldHead += c.soldHead;
    if (c.totalSoldWeightKg !== null && c.totalSoldWeightKg > 0) {
      cur.totalSoldWeightKg += c.totalSoldWeightKg;
      cur.hasSoldWeight = true;
    }
    cur.totalFeedBags += c.feedBags;
    cur.totalFeedCost += c.feedCost;
    cur.totalPigletCost += c.pigletCost;
    cur.totalMedCost += c.medCost;
    cur.totalOtherCosts += c.otherCosts;
    cur.totalRevenue += c.totalRevenue;
    cur.totalProductionCost += c.totalProductionCost;
    cur.netGrossProfit += c.grossProfit;
    if (c.growerNetPayout !== null) {
      cur.totalGrowerPayout += c.growerNetPayout;
    }

    raiserMap.set(key, cur);
  }

  return Array.from(raiserMap.entries()).map(([growerName, val]) => {
    const avgGrossMarginPct = val.totalRevenue > 0
      ? Number(((val.netGrossProfit / val.totalRevenue) * 100).toFixed(2))
      : null;

    return {
      growerName,
      totalCycles: val.cycles.length,
      cycleCodes: val.cycles.map((c) => c.cycleCode),
      totalPlacedHead: val.hasPlacedHead ? val.totalPlacedHead : null,
      totalSoldHead: val.totalSoldHead,
      totalSoldWeightKg: val.hasSoldWeight ? val.totalSoldWeightKg : null,
      totalFeedBags: val.totalFeedBags,
      totalFeedCost: val.totalFeedCost,
      totalPigletCost: val.totalPigletCost,
      totalMedCost: val.totalMedCost,
      totalOtherCosts: val.totalOtherCosts,
      totalRevenue: val.totalRevenue,
      totalProductionCost: val.totalProductionCost,
      netGrossProfit: val.netGrossProfit,
      avgGrossMarginPct,
      totalGrowerPayout: val.totalGrowerPayout,
    };
  }).sort((a, b) => b.totalRevenue - a.totalRevenue);
}

/**
 * Computes executive management KPIs and dynamic batch charts data.
 */
/**
 * Computes executive management KPIs and dynamic batch charts data for a specific year or all years.
 */
export function computeManagementSummary(
  cycles: CyclePerformanceData[],
  ledgerEntries: GeneralLedgerEntry[],
  selectedYear: string = 'all',
  settlements: any[] = [],
  issues: any[] = []
): ManagementSummaryData {
  const isAllYears = selectedYear === 'all';

  // 1. Filter ledger entries for company-wide Booked Revenue & Booked Costs
  const activeLedgerEntries = isAllYears
    ? ledgerEntries
    : ledgerEntries.filter((e) => e.date && e.date.startsWith(selectedYear));

  // 2. Filter cycles associated with the selected reporting year (by batch code prefix or placement start date)
  const cohortCycles = isAllYears
    ? cycles
    : cycles.filter((c) =>
        (c.batchCode && c.batchCode.startsWith(selectedYear)) ||
        (!c.batchCode.startsWith('2025') && !c.batchCode.startsWith('2026') && c.startDate && c.startDate.startsWith(selectedYear))
      );

  // Canonical Ledger Totals for Booked Revenue & Costs
  const totalBookedRevenue = activeLedgerEntries
    .filter((e) => e.ledgerNr === '1010' || e.ledgerNr === '1011')
    .reduce((sum, e) => sum + e.total, 0);

  const directCostNrs = ['3010', '3012', '3015', '3020', '3021', '3040', '3030', '3031', '3032', '3034', '3050', '3051'];
  const totalBookedProductionCosts = activeLedgerEntries
    .filter((e) => directCostNrs.includes(e.ledgerNr || ''))
    .reduce((sum, e) => sum + e.total, 0);

  const netBookedMovement = totalBookedRevenue - totalBookedProductionCosts;

  // 1. Financial Snapshot: Completed-Cycle True Profitability
  const completedCycles = cohortCycles.filter((c) => c.status === 'settled' || c.status === 'closed');
  const completedCyclesRevenue = completedCycles.reduce((sum, c) => sum + c.totalRevenue, 0);
  const completedCyclesCost = completedCycles.reduce((sum, c) => sum + c.totalProductionCost, 0);
  const completedCyclesGrossProfit = completedCycles.reduce((sum, c) => sum + c.grossProfit, 0);
  const completedCyclesGrossMarginPct = completedCyclesRevenue > 0
    ? Number(((completedCyclesGrossProfit / completedCyclesRevenue) * 100).toFixed(2))
    : null;
  const overallClosedGrossMarginPct = completedCyclesGrossMarginPct;

  // 2. Current Production: Live / Source-backed operational data
  const activeRunningCycles = cohortCycles.filter((c) => c.status === 'active' || c.status === 'selling');
  const activeCyclesCount = activeRunningCycles.length;
  const livePigsOnFeed = activeRunningCycles.reduce((sum, c) => {
    const placed = c.placedHead || 0;
    const mort = c.mortalityHead || 0;
    const sold = c.soldHead || 0;
    return sum + Math.max(0, placed - mort - sold);
  }, 0);
  const totalPigsSold = cohortCycles.reduce((sum, c) => sum + c.soldHead, 0);
  const totalMortalityHead = cohortCycles.reduce((sum, c) => sum + (c.mortalityHead || 0), 0);
  const totalPlacedHead = cohortCycles.reduce((sum, c) => sum + (c.placedHead || 0), 0);
  const hasValidMortalityRate = totalPlacedHead > 0 && totalMortalityHead >= 0;
  const overallMortalityRatePct = hasValidMortalityRate
    ? Number(((totalMortalityHead / totalPlacedHead) * 100).toFixed(2))
    : null;

  const totalFeedAllocatedCost = cohortCycles.reduce((sum, c) => sum + c.feedCost, 0);
  const totalFeedBags = cohortCycles.reduce((sum, c) => sum + c.feedBags, 0);

  // 3. Grower Settlement Position (derived strictly from calculated settlement obligations)
  const cohortCycleIdSet = new Set(cohortCycles.map((c) => c.cycleId));
  const activeSettlements = settlements.filter((s) => !s.is_void && (isAllYears || cohortCycleIdSet.has(s.cycle_id)));

  const finalizedCount = activeSettlements.filter((s) => ['frozen', 'settled'].includes(s.status)).length;
  const approvedCount = activeSettlements.filter((s) => s.status === 'approved').length;
  const draftCount = activeSettlements.filter((s) => s.status === 'draft').length;
  const amountOutstanding = activeSettlements
    .filter((s) => ['draft', 'approved'].includes(s.status))
    .reduce((sum, s) => sum + (Number(s.grower_net_payout) || 0), 0);

  const settlementPosition: GrowerSettlementSummaryPosition = {
    finalizedCount,
    approvedCount,
    draftCount,
    amountOutstanding,
  };

  // 4. Completed Cycle Performance & Callouts
  const validMarginCycles = completedCycles.filter((c) => c.grossMarginPct !== null && c.totalRevenue > 0);
  const sortedByMarginDesc = [...validMarginCycles].sort((a, b) => (b.grossMarginPct || 0) - (a.grossMarginPct || 0));
  const sortedByMarginAsc = [...validMarginCycles].sort((a, b) => (a.grossMarginPct || 0) - (b.grossMarginPct || 0));

  const bestMarginCycle = sortedByMarginDesc.length > 0
    ? {
        cycleCode: sortedByMarginDesc[0].cycleCode,
        growerName: sortedByMarginDesc[0].growerName,
        grossMarginPct: sortedByMarginDesc[0].grossMarginPct!,
      }
    : null;

  const lowestMarginCycle = sortedByMarginAsc.length > 0
    ? {
        cycleCode: sortedByMarginAsc[0].cycleCode,
        growerName: sortedByMarginAsc[0].growerName,
        grossMarginPct: sortedByMarginAsc[0].grossMarginPct!,
      }
    : null;

  const validMortalityCycles = completedCycles.filter(
    (c) => c.placedHead !== null && c.placedHead > 0 && c.mortalityHead !== null && c.mortalityRatePct !== null
  );
  const sortedByMortalityAsc = [...validMortalityCycles].sort(
    (a, b) => (a.mortalityRatePct || 0) - (b.mortalityRatePct || 0)
  );

  const lowestMortalityCycle = sortedByMortalityAsc.length > 0
    ? {
        cycleCode: sortedByMortalityAsc[0].cycleCode,
        growerName: sortedByMortalityAsc[0].growerName,
        mortalityRatePct: sortedByMortalityAsc[0].mortalityRatePct!,
        placedHead: sortedByMortalityAsc[0].placedHead!,
        mortalityHead: sortedByMortalityAsc[0].mortalityHead!,
      }
    : null;

  const completedPerformance: CompletedCyclePerformanceSummary = {
    completedCycles,
    bestMarginCycle,
    lowestMarginCycle,
    lowestMortalityCycle,
    completedCyclesGrossProfit,
    completedCyclesGrossMarginPct,
    completedCyclesRevenue,
    completedCyclesCost,
  };

  // 5. Production Cost Mix & Movement
  const feedCostNrs = ['3020', '3021'];
  const pigletCostNrs = ['3010', '3012', '3015'];
  const medCostNrs = ['3030', '3031', '3032', '3034'];
  const otherCostNrs = ['3040', '3050', '3051'];

  const feedAmount = activeLedgerEntries
    .filter((e) => feedCostNrs.includes(e.ledgerNr || ''))
    .reduce((sum, e) => sum + e.total, 0);

  const pigletAmount = activeLedgerEntries
    .filter((e) => pigletCostNrs.includes(e.ledgerNr || ''))
    .reduce((sum, e) => sum + e.total, 0);

  const medAmount = activeLedgerEntries
    .filter((e) => medCostNrs.includes(e.ledgerNr || ''))
    .reduce((sum, e) => sum + e.total, 0);

  const otherAmount = activeLedgerEntries
    .filter((e) => otherCostNrs.includes(e.ledgerNr || ''))
    .reduce((sum, e) => sum + e.total, 0);

  let priorFeedAmount: number | null = null;
  let priorPigletAmount: number | null = null;
  let priorMedAmount: number | null = null;
  let priorOtherAmount: number | null = null;

  if (!isAllYears && /^\d{4}$/.test(selectedYear)) {
    const priorYearNum = parseInt(selectedYear, 10) - 1;
    const priorYearStr = String(priorYearNum);
    const priorLedger = ledgerEntries.filter((e) => e.date && e.date.startsWith(priorYearStr));

    if (priorLedger.length > 0) {
      priorFeedAmount = priorLedger
        .filter((e) => feedCostNrs.includes(e.ledgerNr || ''))
        .reduce((sum, e) => sum + e.total, 0);

      priorPigletAmount = priorLedger
        .filter((e) => pigletCostNrs.includes(e.ledgerNr || ''))
        .reduce((sum, e) => sum + e.total, 0);

      priorMedAmount = priorLedger
        .filter((e) => medCostNrs.includes(e.ledgerNr || ''))
        .reduce((sum, e) => sum + e.total, 0);

      priorOtherAmount = priorLedger
        .filter((e) => otherCostNrs.includes(e.ledgerNr || ''))
        .reduce((sum, e) => sum + e.total, 0);
    }
  }

  const computeCategory = (
    category: 'feed' | 'piglets' | 'medication' | 'other',
    label: string,
    accountCodes: string,
    amount: number,
    priorAmount: number | null
  ): ProductionCostMixCategory => {
    const pctOfTotalCosts = totalBookedProductionCosts > 0
      ? Number(((amount / totalBookedProductionCosts) * 100).toFixed(1))
      : 0;

    let changePct: number | null = null;
    if (priorAmount !== null && priorAmount > 0) {
      changePct = Number((((amount - priorAmount) / priorAmount) * 100).toFixed(1));
    }

    return {
      category,
      label,
      accountCodes,
      amount,
      pctOfTotalCosts,
      priorYearAmount: priorAmount,
      changePct,
    };
  };

  const costMix: ProductionCostMixCategory[] = [
    computeCategory('feed', 'Feed Deliveries & Usage', '3020, 3021', feedAmount, priorFeedAmount),
    computeCategory('piglets', 'Piglet Stock & Arrivals', '3010, 3012, 3015', pigletAmount, priorPigletAmount),
    computeCategory('medication', 'Veterinary & Medication', '3030–3034', medAmount, priorMedAmount),
    computeCategory('other', 'Farm Operations & Trucking', '3040, 3050, 3051', otherAmount, priorOtherAmount),
  ];

  // 6. Management Attention (using live source-backed records only)
  const activeIssues = issues.filter((i) => i.status === 'needs_review');
  const needsReviewCount = activeIssues.length;

  const incompleteRecordsCount = cohortCycles.filter(
    (c) => c.placedHead === null || !c.startDate
  ).length;

  const outstandingSettlementsCount = cohortCycles.filter(
    (c) => c.operationType === 'contract_grower' && c.status !== 'closed' && c.status !== 'settled' && c.soldHead > 0
  ).length;

  const unresolvedCycleIssuesCount = activeIssues.filter((i) => i.cycle_id != null).length;

  const managementAttention: ManagementAttentionSummary = {
    needsReviewCount,
    incompleteRecordsCount,
    outstandingSettlementsCount,
    unresolvedCycleIssuesCount,
  };

  // Dynamic Batch Comparisons (Preserved)
  const batchMap = new Map<string, {
    revenue: number;
    productionCost: number;
    feedCost: number;
    pigletCost: number;
    medCost: number;
    otherCost: number;
    grossProfit: number;
    cycleCount: number;
    soldHead: number;
  }>();

  for (const c of cohortCycles) {
    const b = c.batchCode || 'Unassigned';
    const cur = batchMap.get(b) || {
      revenue: 0,
      productionCost: 0,
      feedCost: 0,
      pigletCost: 0,
      medCost: 0,
      otherCost: 0,
      grossProfit: 0,
      cycleCount: 0,
      soldHead: 0,
    };

    cur.revenue += c.totalRevenue;
    cur.productionCost += c.totalProductionCost;
    cur.feedCost += c.feedCost;
    cur.pigletCost += c.pigletCost;
    cur.medCost += c.medCost;
    cur.otherCost += c.otherCosts;
    cur.grossProfit += c.grossProfit;
    cur.cycleCount += 1;
    cur.soldHead += c.soldHead;

    batchMap.set(b, cur);
  }

  const batchComparison: BatchComparisonPoint[] = Array.from(batchMap.entries())
    .map(([batchCode, val]) => ({
      batchCode,
      revenue: val.revenue,
      productionCost: val.productionCost,
      feedCost: val.feedCost,
      pigletCost: val.pigletCost,
      medCost: val.medCost,
      otherCost: val.otherCost,
      grossProfit: val.grossProfit,
      marginPct: val.revenue > 0 ? Number(((val.grossProfit / val.revenue) * 100).toFixed(2)) : null,
      cycleCount: val.cycleCount,
      soldHead: val.soldHead,
    }))
    .sort((a, b) => a.batchCode.localeCompare(b.batchCode));

  // Prior Year Overall Comparison (e.g. 2026 vs 2025)
  let priorYear: ManagementSummaryData['priorYear'] = null;
  if (!isAllYears && /^\d{4}$/.test(selectedYear)) {
    const priorYearNum = parseInt(selectedYear, 10) - 1;
    const priorYearStr = String(priorYearNum);

    const priorLedger = ledgerEntries.filter((e) => e.date && e.date.startsWith(priorYearStr));
    if (priorLedger.length > 0) {
      const priorRev = priorLedger
        .filter((e) => e.ledgerNr === '1010' || e.ledgerNr === '1011')
        .reduce((sum, e) => sum + e.total, 0);

      const priorCosts = priorLedger
        .filter((e) => directCostNrs.includes(e.ledgerNr || ''))
        .reduce((sum, e) => sum + e.total, 0);

      const priorNet = priorRev - priorCosts;

      const priorCycles = cycles.filter((c) =>
        (c.batchCode && c.batchCode.startsWith(priorYearStr)) ||
        (!c.batchCode.startsWith('2025') && !c.batchCode.startsWith('2026') && c.startDate && c.startDate.startsWith(priorYearStr))
      );

      const priorPigs = priorCycles.reduce((sum, c) => sum + c.soldHead, 0);
      const priorFeed = priorCycles.reduce((sum, c) => sum + c.feedCost, 0);

      const revenueGrowthPct = priorRev > 0
        ? Number((((totalBookedRevenue - priorRev) / priorRev) * 100).toFixed(1))
        : null;

      const costGrowthPct = priorCosts > 0
        ? Number((((totalBookedProductionCosts - priorCosts) / priorCosts) * 100).toFixed(1))
        : null;

      priorYear = {
        year: priorYearStr,
        totalBookedRevenue: priorRev,
        totalBookedProductionCosts: priorCosts,
        netBookedMovement: priorNet,
        revenueGrowthPct,
        costGrowthPct,
        totalPigsSold: priorPigs,
        totalFeedAllocatedCost: priorFeed,
      };
    }
  }

  return {
    selectedYear,
    totalBookedRevenue,
    totalBookedProductionCosts,
    netBookedMovement,
    completedCyclesGrossProfit,
    completedCyclesGrossMarginPct,
    completedCyclesRevenue,
    completedCyclesCost,
    overallClosedGrossMarginPct,
    activeCyclesCount,
    livePigsOnFeed,
    totalPigsSold,
    totalMortalityHead,
    hasValidMortalityRate,
    overallMortalityRatePct,
    settlementPosition,
    completedPerformance,
    costMix,
    managementAttention,
    totalFeedAllocatedCost,
    totalFeedBags,
    totalCyclesCount: cohortCycles.length,
    batchComparison,
    priorYear,
  };
}

/**
 * Computes Annual Financial Summary across all operational years.
 */
export function computeAnnualFinancialSummary(entries: GeneralLedgerEntry[]): AnnualFinancialSummaryRecord[] {
  const yearMap = new Map<number, {
    pigSales: number;
    totalCosts: number;
    monthsSet: Set<number>;
  }>();

  const directCostNrs = ['3010', '3012', '3015', '3020', '3021', '3040', '3030', '3031', '3032', '3034', '3050', '3051'];

  for (const e of entries) {
    if (!e.date || e.date.length < 7) continue;
    const year = parseInt(e.date.substring(0, 4), 10);
    const month = parseInt(e.date.substring(5, 7), 10);
    if (isNaN(year)) continue;

    const cur = yearMap.get(year) || { pigSales: 0, totalCosts: 0, monthsSet: new Set<number>() };
    cur.monthsSet.add(month);

    const code = e.ledgerNr || '';
    const amount = e.total || 0;

    if (code === '1010' || code === '1011') {
      cur.pigSales += amount;
    } else if (directCostNrs.includes(code)) {
      cur.totalCosts += amount;
    }

    yearMap.set(year, cur);
  }

  return Array.from(yearMap.entries())
    .map(([year, val]) => {
      const netBookedMovement = val.pigSales - val.totalCosts;
      const grossMarginPct = val.pigSales > 0 ? Number(((netBookedMovement / val.pigSales) * 100).toFixed(1)) : null;
      return {
        year,
        pigSales: val.pigSales,
        totalBookedCosts: val.totalCosts,
        netBookedMovement,
        grossMarginPct,
        activeMonthsCount: val.monthsSet.size,
      };
    })
    .sort((a, b) => b.year - a.year);
}

/**
 * Computes Monthly Financial Activity consuming the canonical ledger entries.
 * Can filter by a specific calendar year or return all months with YoY comparisons.
 */
export function computeMonthlyFinancialActivity(
  entries: GeneralLedgerEntry[],
  selectedYear: string = 'all'
): MonthlyFinancialActivityRecord[] {
  const monthMap = new Map<string, {
    year: number;
    month: number;
    pigSales: number;
    piglets: number;
    feeds: number;
    medsAndDirectExpenses: number;
    adjustments: number;
    headSold: number;
    hasHeadSold: boolean;
    feedBags: number;
    hasFeedBags: boolean;
    breakdown: Record<string, { code: string; name: string; sum: number; count: number }>;
  }>();

  for (const entry of entries) {
    if (!entry.date) continue;
    const monthKey = entry.date.substring(0, 7); // YYYY-MM
    if (!monthKey || monthKey.length < 7) continue;

    const [yearStr, monthStr] = monthKey.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);

    const cur = monthMap.get(monthKey) || {
      year,
      month,
      pigSales: 0,
      piglets: 0,
      feeds: 0,
      medsAndDirectExpenses: 0,
      adjustments: 0,
      headSold: 0,
      hasHeadSold: false,
      feedBags: 0,
      hasFeedBags: false,
      breakdown: {},
    };

    const code = entry.ledgerNr || 'OTHER';
    const amount = entry.total || 0;

    // Account mapping
    if (code === '1010' || code === '1011') {
      cur.pigSales += amount;
      if (entry.qty && entry.qty > 0) {
        cur.headSold += entry.qty;
        cur.hasHeadSold = true;
      }
    } else if (code === '3010' || code === '3012' || code === '3015') {
      cur.piglets += amount;
    } else if (code === '3020' || code === '3021') {
      cur.feeds += amount;
      if (code === '3020' && entry.qty && entry.qty > 0) {
        cur.feedBags += entry.qty;
        cur.hasFeedBags = true;
      }
    } else if (['3030', '3031', '3032', '3034', '3040', '3050', '3051'].includes(code)) {
      cur.medsAndDirectExpenses += amount;
    } else if (['4010', '4020', '4030', '4040', '4050'].includes(code)) {
      cur.adjustments += amount;
    }

    if (!cur.breakdown[code]) {
      cur.breakdown[code] = { code, name: entry.ledgerDescription || (entry as any).ledgerDesc || 'Transaction', sum: 0, count: 0 };
    }
    cur.breakdown[code].sum += amount;
    cur.breakdown[code].count += 1;

    monthMap.set(monthKey, cur);
  }

  // Convert to sorted records (descending by month)
  const sortedKeys = Array.from(monthMap.keys()).sort().reverse();

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const allRecords: MonthlyFinancialActivityRecord[] = sortedKeys.map((key) => {
    const d = monthMap.get(key)!;
    const monthLabel = `${monthNames[d.month - 1]} ${d.year}`;
    const totalBookedCosts = d.piglets + d.feeds + d.medsAndDirectExpenses;
    const netBookedMovement = d.pigSales - totalBookedCosts;

    return {
      monthKey: key,
      monthLabel,
      year: d.year,
      month: d.month,
      pigSales: d.pigSales,
      salesRevenue: d.pigSales,
      piglets: d.piglets,
      pigletCost: d.piglets,
      feeds: d.feeds,
      feedCost: d.feeds,
      medsAndDirectExpenses: d.medsAndDirectExpenses,
      medCost: d.medsAndDirectExpenses,
      totalBookedCosts,
      totalProductionCost: totalBookedCosts,
      netBookedMovement,
      grossProfit: netBookedMovement,
      grossMarginPct: d.pigSales > 0 ? Number(((netBookedMovement / d.pigSales) * 100).toFixed(1)) : null,
      adjustments: d.adjustments,
      headSold: d.hasHeadSold ? d.headSold : null,
      feedBags: d.hasFeedBags ? d.feedBags : null,
      momRevenueGrowthPct: null,
      momCostGrowthPct: null,
      yoyRevenueGrowthPct: null,
      yoyCostGrowthPct: null,
      accountBreakdown: d.breakdown,
    };
  });

  // Calculate MoM and YoY growth indicators across the entire multi-year sequence
  for (let i = 0; i < allRecords.length; i++) {
    const cur = allRecords[i];
    const prevMonth = allRecords[i + 1]; // next in descending array is previous month
    if (prevMonth) {
      if (prevMonth.pigSales > 0) {
        cur.momRevenueGrowthPct = Number((((cur.pigSales - prevMonth.pigSales) / prevMonth.pigSales) * 100).toFixed(1));
      }
      if (prevMonth.totalBookedCosts > 0) {
        cur.momCostGrowthPct = Number((((cur.totalBookedCosts - prevMonth.totalBookedCosts) / prevMonth.totalBookedCosts) * 100).toFixed(1));
      }
    }

    // Find same month last year
    const lastYear = allRecords.find((r) => r.year === cur.year - 1 && r.month === cur.month);
    if (lastYear) {
      if (lastYear.pigSales > 0) {
        cur.yoyRevenueGrowthPct = Number((((cur.pigSales - lastYear.pigSales) / lastYear.pigSales) * 100).toFixed(1));
      }
      if (lastYear.totalBookedCosts > 0) {
        cur.yoyCostGrowthPct = Number((((cur.totalBookedCosts - lastYear.totalBookedCosts) / lastYear.totalBookedCosts) * 100).toFixed(1));
      }
    }
  }

  // Filter to selected year if not 'all'
  if (selectedYear !== 'all' && /^\d{4}$/.test(selectedYear)) {
    const targetYear = parseInt(selectedYear, 10);
    return allRecords.filter((r) => r.year === targetYear);
  }

  return allRecords;
}

export const computeMonthlyFinancialOverview = computeMonthlyFinancialActivity;

/**
 * Filters cycle performance data by multiple criteria, including Reporting Year.
 */
export function filterCyclePerformanceData(
  data: CyclePerformanceData[],
  filters: ReportFilters
): CyclePerformanceData[] {
  return data.filter((item) => {
    // 1. Reporting Year Filter (checks batch prefix or placement year)
    if (filters.selectedYear && filters.selectedYear !== 'all') {
      const targetYear = filters.selectedYear;
      const matchBatchYear = item.batchCode && item.batchCode.startsWith(targetYear);
      const matchStartYear = item.startDate && item.startDate.startsWith(targetYear);
      if (!matchBatchYear && !matchStartYear) {
        return false;
      }
    }
    if (filters.batchCode && filters.batchCode !== 'all' && item.batchCode !== filters.batchCode) {
      return false;
    }
    if (filters.farmId && filters.farmId !== 'all' && item.farmId !== filters.farmId) {
      return false;
    }
    if (filters.growerId && filters.growerId !== 'all' && item.growerId !== filters.growerId) {
      return false;
    }
    if (filters.status && filters.status !== 'all' && item.status !== filters.status) {
      return false;
    }
    if (filters.operationType && filters.operationType !== 'all' && item.operationType !== filters.operationType) {
      return false;
    }
    if (filters.startDateFrom && item.startDate < filters.startDateFrom) {
      return false;
    }
    if (filters.startDateTo && item.startDate > filters.startDateTo) {
      return false;
    }
    if (filters.saleDateFrom && (!item.endDate || item.endDate < filters.saleDateFrom)) {
      return false;
    }
    if (filters.saleDateTo && (!item.endDate || item.endDate > filters.saleDateTo)) {
      return false;
    }
    if (filters.search && filters.search.trim()) {
      const q = filters.search.toLowerCase().trim();
      const matchCode = item.cycleCode.toLowerCase().includes(q);
      const matchBatch = item.batchCode.toLowerCase().includes(q);
      const matchGrower = item.growerName.toLowerCase().includes(q);
      const matchFarm = item.farmName.toLowerCase().includes(q);
      if (!matchCode && !matchBatch && !matchGrower && !matchFarm) {
        return false;
      }
    }
    return true;
  });
}

/**
 * Generic paginated fetcher for PostgREST to bypass the 1,000 row default limit.
 */
async function fetchAllWithPagination<T>(table: string, selectQuery: string, checkIsVoid: boolean = true): Promise<T[]> {
  const PAGE_SIZE = 1000;
  let allRows: T[] = [];
  let from = 0;
  let hasMore = true;

  while (hasMore) {
    let query = supabase.from(table).select(selectQuery);
    if (checkIsVoid) {
      query = query.eq('is_void', false);
    }
    const { data, error } = await query.range(from, from + PAGE_SIZE - 1);

    if (error) throw error;
    if (!data || data.length === 0) {
      hasMore = false;
    } else {
      allRows = allRows.concat(data as T[]);
      if (data.length < PAGE_SIZE) {
        hasMore = false;
      } else {
        from += PAGE_SIZE;
      }
    }
  }
  return allRows;
}

export const reportService = {
  /**
   * Fetches and aggregates complete dataset from Supabase for all 105 cycles and transactions.
   * Guarantees 100% data coverage without PostgREST row clipping.
   */
  async fetchCompleteReportsDataset(): Promise<{
    cyclesPerformance: CyclePerformanceData[];
    raiserPerformance: RaiserPerformanceData[];
    managementSummary: ManagementSummaryData;
    monthlyFinancialActivity: MonthlyFinancialActivityRecord[];
    annualFinancialSummary: AnnualFinancialSummaryRecord[];
    availableYears: string[];
    defaultYear: string;
    ledgerEntries: GeneralLedgerEntry[];
    settlements: any[];
    reconciliationIssues: any[];
  }> {
    // 1. Fetch live Ledger entries from canonical ledger service
    const ledgerEntries = await ledgerService.getLedgerEntries();

    // 2. Fetch operational tables with complete pagination
    const [
      cycles,
      arrivals,
      mortalities,
      transfers,
      feedReceipts,
      medicineReceipts,
      expenses,
      sales,
      settlements,
      issues,
    ] = await Promise.all([
      fetchAllWithPagination<any>(
        'production_cycles',
        'id, cycle_code, batch_code, grower_id, farm_id, operation_type, status, start_date, expected_sale_date, actual_close_date, legacy_raiser_name, is_void, growers(full_name), farms(farm_name)'
      ),
      fetchAllWithPagination<any>('pig_arrivals', 'id, cycle_id, arrival_date, head_count, total_weight_kg, total_cost, is_void'),
      fetchAllWithPagination<any>('mortality_events', 'id, cycle_id, event_date, head_count, is_void'),
      fetchAllWithPagination<any>('feed_transfers', 'id, source_cycle_id, destination_cycle_id, quantity_bags, total_transferred_cost, is_void'),
      fetchAllWithPagination<any>('feed_receipts', 'id, cycle_id, receipt_date, quantity_bags, total_cost, is_void'),
      fetchAllWithPagination<any>('medicine_receipts', 'id, cycle_id, receipt_date, quantity, total_amount, is_void'),
      fetchAllWithPagination<any>('expenses', 'id, cycle_id, expense_date, amount, account_code, is_void'),
      fetchAllWithPagination<any>('pig_sales', 'id, cycle_id, sale_date, head_count, net_weight_kg, total_amount, is_void'),
      fetchAllWithPagination<any>(
        'settlements',
        'id, cycle_id, settlement_date, status, total_sales_revenue, total_feed_cost, total_piglet_cost, total_med_cost, total_other_costs, gross_profit, grower_net_payout, is_void'
      ),
      fetchAllWithPagination<any>(
        'reconciliation_issues',
        'id, cycle_id, issue_type, severity, status',
        false
      ),
    ]);

    // Build cycle lookup maps
    const arrivalsByCycle = new Map<string, any[]>();
    for (const a of arrivals) {
      if (!a.cycle_id) continue;
      const list = arrivalsByCycle.get(a.cycle_id) || [];
      list.push(a);
      arrivalsByCycle.set(a.cycle_id, list);
    }

    const mortalitiesByCycle = new Map<string, any[]>();
    for (const m of mortalities) {
      if (!m.cycle_id) continue;
      const list = mortalitiesByCycle.get(m.cycle_id) || [];
      list.push(m);
      mortalitiesByCycle.set(m.cycle_id, list);
    }

    const transfersInByCycle = new Map<string, any[]>();
    const transfersOutByCycle = new Map<string, any[]>();
    for (const t of transfers) {
      if (t.destination_cycle_id) {
        const inList = transfersInByCycle.get(t.destination_cycle_id) || [];
        inList.push(t);
        transfersInByCycle.set(t.destination_cycle_id, inList);
      }
      if (t.source_cycle_id) {
        const outList = transfersOutByCycle.get(t.source_cycle_id) || [];
        outList.push(t);
        transfersOutByCycle.set(t.source_cycle_id, outList);
      }
    }

    const feedReceiptsByCycle = new Map<string, any[]>();
    for (const r of feedReceipts) {
      if (!r.cycle_id) continue;
      const list = feedReceiptsByCycle.get(r.cycle_id) || [];
      list.push(r);
      feedReceiptsByCycle.set(r.cycle_id, list);
    }

    const medReceiptsByCycle = new Map<string, any[]>();
    for (const m of medicineReceipts) {
      if (!m.cycle_id) continue;
      const list = medReceiptsByCycle.get(m.cycle_id) || [];
      list.push(m);
      medReceiptsByCycle.set(m.cycle_id, list);
    }

    const expensesByCycle = new Map<string, any[]>();
    for (const e of expenses) {
      if (!e.cycle_id) continue;
      const list = expensesByCycle.get(e.cycle_id) || [];
      list.push(e);
      expensesByCycle.set(e.cycle_id, list);
    }

    const salesByCycle = new Map<string, any[]>();
    for (const s of sales) {
      if (!s.cycle_id) continue;
      const list = salesByCycle.get(s.cycle_id) || [];
      list.push(s);
      salesByCycle.set(s.cycle_id, list);
    }

    const settlementsByCycle = new Map<string, any[]>();
    for (const st of settlements) {
      if (!st.cycle_id) continue;
      const list = settlementsByCycle.get(st.cycle_id) || [];
      list.push(st);
      settlementsByCycle.set(st.cycle_id, list);
    }

    // Sort cycles by start_date descending
    cycles.sort((a, b) => (b.start_date || '').localeCompare(a.start_date || ''));

    // Compute cycle performance
    const cyclesPerformance: CyclePerformanceData[] = cycles.map((c) =>
      computeSingleCyclePerformance(
        c,
        arrivalsByCycle.get(c.id) || [],
        mortalitiesByCycle.get(c.id) || [],
        transfersInByCycle.get(c.id) || [],
        transfersOutByCycle.get(c.id) || [],
        feedReceiptsByCycle.get(c.id) || [],
        medReceiptsByCycle.get(c.id) || [],
        expensesByCycle.get(c.id) || [],
        salesByCycle.get(c.id) || [],
        settlementsByCycle.get(c.id) || []
      )
    );

    // Extract available years dynamically
    const yearsSet = new Set<string>();
    for (const e of ledgerEntries) {
      if (e.date && e.date.length >= 4) {
        const y = e.date.substring(0, 4);
        if (/^\d{4}$/.test(y)) yearsSet.add(y);
      }
    }
    for (const c of cyclesPerformance) {
      if (c.batchCode && c.batchCode.length >= 4) {
        const y = c.batchCode.substring(0, 4);
        if (/^\d{4}$/.test(y)) yearsSet.add(y);
      }
      if (c.startDate && c.startDate.length >= 4) {
        const y = c.startDate.substring(0, 4);
        if (/^\d{4}$/.test(y)) yearsSet.add(y);
      }
    }

    const availableYears = Array.from(yearsSet).sort().reverse();
    const defaultYear = availableYears[0] || '2026';

    // Compute Management Summary KPIs & Charts (defaults to latest year with data)
    const managementSummary = computeManagementSummary(
      cyclesPerformance,
      ledgerEntries,
      defaultYear,
      settlements,
      issues
    );

    // Compute Monthly Financial Activity (defaults to latest year with data)
    const monthlyFinancialActivity = computeMonthlyFinancialActivity(ledgerEntries, defaultYear);

    // Compute Raiser Performance across all cycles
    const raiserPerformance = aggregatePerformanceByRaiser(cyclesPerformance);

    // Compute Annual Financial Summary across all years
    const annualFinancialSummary = computeAnnualFinancialSummary(ledgerEntries);

    return {
      cyclesPerformance,
      raiserPerformance,
      managementSummary,
      monthlyFinancialActivity,
      annualFinancialSummary,
      availableYears,
      defaultYear,
      ledgerEntries,
      settlements,
      reconciliationIssues: issues,
    };
  },
};

/**
 * Export Performance Report to CSV.
 */
export function exportPerformanceReportToCSV(data: CyclePerformanceData[], filename = 'VSB_Production_Performance.csv'): void {
  const headers = [
    'Production Cycle',
    'Batch',
    'Raiser',
    'Farm',
    'Status',
    'Start Date',
    'Close Date',
    'Pigs Placed',
    'Pigs Sold',
    'Mortality Head',
    'Mortality %',
    'Feed Bags',
    'Feed Cost (₱)',
    'Piglet Cost (₱)',
    'Meds Cost (₱)',
    'Other Expenses (₱)',
    'Total Revenue (₱)',
    'Total Costs (₱)',
    'Gross Profit (₱)',
    'Gross Margin %',
    'Grower Payout (₱)',
    'Days on Feed',
  ];

  const rows = data.map((d) => [
    `"${d.cycleCode}"`,
    `"${d.batchCode}"`,
    `"${d.growerName.replace(/"/g, '""')}"`,
    `"${d.farmName.replace(/"/g, '""')}"`,
    `"${d.status}"`,
    `"${d.startDate}"`,
    `"${d.endDate || '—'}"`,
    d.placedHead !== null ? d.placedHead : '—',
    d.soldHead,
    d.mortalityHead !== null ? d.mortalityHead : '—',
    d.mortalityRatePct !== null ? `${d.mortalityRatePct.toFixed(2)}%` : '—',
    d.feedBags,
    d.feedCost.toFixed(2),
    d.pigletCost.toFixed(2),
    d.medCost.toFixed(2),
    d.otherCosts.toFixed(2),
    d.totalRevenue.toFixed(2),
    d.totalProductionCost.toFixed(2),
    d.grossProfit.toFixed(2),
    d.grossMarginPct !== null ? `${d.grossMarginPct.toFixed(2)}%` : '—',
    d.growerNetPayout !== null ? d.growerNetPayout.toFixed(2) : '—',
    d.daysOnFeed !== null ? d.daysOnFeed : '—',
  ]);

  const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export Performance Report to Excel (.xlsx).
 */
export function exportPerformanceReportToExcel(data: CyclePerformanceData[], filename = 'VSB_Production_Performance.xlsx'): void {
  const tableRows = data.map((d) => ({
    'Production Cycle': d.cycleCode,
    'Batch Code': d.batchCode,
    'Raiser': d.growerName,
    'Farm': d.farmName,
    'Status': d.status,
    'Start Date': d.startDate,
    'Close Date': d.endDate || '—',
    'Pigs Placed': d.placedHead !== null ? d.placedHead : '—',
    'Pigs Sold': d.soldHead,
    'Mortality Head': d.mortalityHead !== null ? d.mortalityHead : '—',
    'Mortality %': d.mortalityRatePct !== null ? `${d.mortalityRatePct}%` : '—',
    'Feed Bags': d.feedBags,
    'Feed Cost (₱)': d.feedCost,
    'Piglet Cost (₱)': d.pigletCost,
    'Meds Cost (₱)': d.medCost,
    'Other Expenses (₱)': d.otherCosts,
    'Total Revenue (₱)': d.totalRevenue,
    'Total Costs (₱)': d.totalProductionCost,
    'Gross Profit (₱)': d.grossProfit,
    'Gross Margin %': d.grossMarginPct !== null ? `${d.grossMarginPct}%` : '—',
    'Grower Payout (₱)': d.growerNetPayout !== null ? d.growerNetPayout : '—',
    'Days on Feed': d.daysOnFeed !== null ? d.daysOnFeed : '—',
  }));

  const ws = XLSX.utils.json_to_sheet(tableRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Production Performance');
  XLSX.writeFile(wb, filename);
}
